import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SearchableDropdownInput from "../../components/theme/SearchableDropdownInput";
import setHeader from "../../components/Functions/setHeader";
import axiosConfig from "../../services/axiosConfig";
import LoadingComponent from "../../components/common/LoadingComponent";
import SweetAlert from "sweetalert2";

// Main Purchase Form Component
const PurchaseAdd = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract ID from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const editId = urlParams.get('id');
  const isEditMode = Boolean(editId);

  const [loadingSToreData, setLoadingStoreData] = useState(true);
  const [loadingProductData, setLoadingProductData] = useState(true);
  const [loadingEditData, setLoadingEditData] = useState(isEditMode);
  const [suppliers, setSuppliers] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [products, setProducts] = useState([]);
  
  // Supplier autocomplete states
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);

  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    supplier: '',
    date: today,
    remarks: '',
    items: [{ product: '', quantity: 1, price: 0 }]
  });

  useEffect(() => {
    getStoreData();
  }, []);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (isEditMode && editId) {
      getEditData(editId);
    }
  }, [isEditMode, editId]);

  const getStoreData = () => {
    setLoadingStoreData(true);
    setSuppliers([]);

    axiosConfig.get("all-suppliers", setHeader())
      .then((res) => {
        setSuppliers(res.data.data);
      })
      .catch((error) => {
        console.error("Error fetching store data:", error);
      })
      .finally(() => {
        setLoadingStoreData(false);
      });
  };

  const getData = () => {
    setLoadingProductData(true);
    setProducts([]);

    axiosConfig.get("product-list", setHeader())
      .then((res) => {
        setProducts(res.data.data);
      })
      .catch((error) => {
        console.error("Error fetching product data:", error);
      })
      .finally(() => {
        setLoadingProductData(false);
      });
  };

  const getEditData = (id) => {
    setLoadingEditData(true);

    axiosConfig.get(`get-purchase/${id}`, setHeader())
      .then((res) => {
        const purchaseData = res.data.data;

        // Find the supplier name for the selected supplier ID
        // const selectedSupplier = suppliers.find(supplier => supplier.storeId === purchaseData.store_id);
        // setSupplierSearchTerm(selectedSupplier ? selectedSupplier.store_name : '');

        // Transform the API response to match form structure
        setFormData({
          supplier: purchaseData.supplier || '',
          date: purchaseData.purchase_date || '',
          remarks: purchaseData.description || '',
          items: purchaseData.products && purchaseData.products.length > 0
            ? purchaseData.products.map(item => ({
              product: item.product_id || '',
              quantity: parseInt(item.qty) || 1,
              price: parseFloat(item.price) || 0
            }))
            : [{ product: '', quantity: 1, price: 0 }]
        });
      })
      .catch((error) => {
        console.error("Error fetching edit data:", error);
        SweetAlert.fire({
          title: "Error",
          text: "Failed to load purchase data for editing.",
          icon: "error",
        });
      })
      .finally(() => {
        setLoadingEditData(false);
      });
  };

  // Supplier autocomplete functions
  const filterSuppliers = (searchTerm) => {
    if (searchTerm.trim() === '') {
      setFilteredSuppliers([]);
      setShowSupplierSuggestions(false);
      return;
    }
    
    const filtered = suppliers.filter(supplier =>
      supplier.supplier.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSuppliers(filtered);
    setShowSupplierSuggestions(filtered.length > 0);
  };

  const handleSupplierInputChange = (e) => {
    const value = e.target.value;
    
    setSupplierSearchTerm(value);
    filterSuppliers(value);
    
    // Clear actual form value when typing
    setFormData(prev => ({ ...prev, supplier: '' }));
  };

  const handleSupplierSelect = (supplier) => {
  setSupplierSearchTerm(supplier.supplier);
  setShowSupplierSuggestions(false);
  setFormData(prev => ({ ...prev, supplier: supplier.supplier }));
};

  // Transform products for dropdown with filtering for selected products
  const getAvailableProducts = (currentIndex) => {
    // Get all selected product IDs except for the current row
    const selectedProductIds = formData.items
      .map((item, index) => index !== currentIndex && item.product ? item.product : null)
      .filter(Boolean);

    // Filter out already selected products
    const availableProducts = products.filter(product =>
      !selectedProductIds.includes(product.id.toString()) && !selectedProductIds.includes(product.id)
    );

    return availableProducts.map(product => ({
      value: product.id,
      label: `${product.productCode} - ${product.productName}`
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const addRow = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product: '', quantity: 1, price: 0 }]
    }));
  };

  const removeRow = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    }
  };

 const handleSave = () => {
  setSaveLoading(true);
  
  // Use the typed supplier name (from input field) as final value
  const finalSupplierName = supplierSearchTerm.trim();
  // Validate form data - check the typed value
  if (!finalSupplierName || !formData.date) {
    SweetAlert.fire({
      title: "Validation Error",
      text: "Please fill in all required fields (Supplier and Date)",
      icon: "warning",
    });
    setSaveLoading(false);
    return;
  }

  // Check if all items have products selected
  const hasEmptyProducts = formData.items.some(item => !item.product);
  if (hasEmptyProducts) {
    SweetAlert.fire({
      title: "Validation Error",
      text: "Please select products for all items",
      icon: "warning",
    });
    setSaveLoading(false);
    return;
  }

  // Transform data to match API format - use the typed supplier name
  const requestData = {
    purchase_id: editId,
    supplier: finalSupplierName, // Use whatever user typed
    purchase_date: formData.date,
    remarks: formData.remarks,
    items: formData.items.map(item => ({
      product: parseInt(item.product),
      qty: parseInt(item.quantity),
      price: parseInt(item.price)
    }))
  };

  // Rest of the save function remains the same...
  const apiEndpoint = isEditMode ? `update-purchase` : "save-purchase";

  axiosConfig.post(apiEndpoint, requestData, setHeader())
    .then((res) => {
      SweetAlert.fire({
        title: "Success",
        text: res.data.messages || `Purchase ${isEditMode ? 'updated' : 'saved'} successfully`,
        icon: "success",
      }).then(() => {
        if (isEditMode) {
          navigate('/pages/purchase-list');
        } else {
          // Reset form for new entries
          setFormData({
            supplier: '',
            date: today,
            remarks: '',
            items: [{ product: '', quantity: 1, price: 0 }]
          });
          setSupplierSearchTerm('');
        }
      });
    })
    .catch((error) => {
      console.error(`Error ${isEditMode ? 'updating' : 'saving'} purchase:`, error);
      SweetAlert.fire({
        title: "Error",
        text: `Error ${isEditMode ? 'updating' : 'saving'} purchase. Please try again.`,
        icon: "error",
      });
    })
    .finally(() => {
      setSaveLoading(false);
    });
};

  // Show loading state first
  if (loadingSToreData || loadingProductData || loadingEditData) {
    return <LoadingComponent />;
  }

  return (
    <>
      <div className="container-fluid" style={{ maxWidth: '1200px' }}>
        {/* Header */}
        <div className="d-flex align-items-center mb-4 pb-1 border-bottom">
          <h1 className="h3 mb-0 text-dark fw-semibold" style={{ marginTop: '2rem' }}>
            {isEditMode ? 'Edit Purchase' : 'Purchase'}
          </h1>
        </div>

        {/* Form Fields */}
        <div className="row g-3 mb-1">
          {/* Supplier Field with Autocomplete */}
          <div className="col-md-4">
            <label htmlFor="supplier" className="form-label fw-medium">Supplier *</label>
            <div className="position-relative">
              <input
                id="supplier"
                type="text"
                value={supplierSearchTerm}
                onChange={handleSupplierInputChange}
                onBlur={() => setTimeout(() => setShowSupplierSuggestions(false), 200)}
                onFocus={() => supplierSearchTerm && setShowSupplierSuggestions(filteredSuppliers.length > 0)}
                className="form-control"
                placeholder="Type to search suppliers..."
                autoComplete="off"
                required
              />
              
              {showSupplierSuggestions && (
                <div 
                  className="position-absolute w-100 bg-white border shadow-sm"
                  style={{ 
                    zIndex: 1000, 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    top: '100%'
                  }}
                >
                  {filteredSuppliers.map((supplier, index) => (
                    <div
                      key={supplier.storeId}
                      className="px-3 py-2 border-bottom"
                      onClick={() => handleSupplierSelect(supplier)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                    >
                      {supplier.supplier}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Hidden input for form validation */}
          <input
  type="hidden"
  name="supplier"
  value={formData.supplier}  // Changed from formData.store to formData.supplier
  required
/>
          </div>

          <div className="col-md-4">
            <label htmlFor="date" className="form-label fw-medium">Date *</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="form-control"
              required
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="remarks" className="form-label fw-medium">Remarks</label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows={1}
              className="form-control"
            />
          </div>
        </div>

        {/* Items Table */}
        <div className="card mb-1">
          <div className="card-body p-0">
            {/* Table Header */}
            <div className="bg-light border-bottom p-2">
              <div className="row align-items-center">
                <div className="col">
                  <span className="fw-semibold text-dark">Product *</span>
                </div>
                <div className="col-auto" style={{ width: '120px' }}>
                  <span className="fw-semibold text-dark">Price</span>
                </div>
                <div className="col-auto" style={{ width: '150px' }}>
                  <span className="fw-semibold text-dark">Quantity</span>
                </div>
                <div className="col-auto" style={{ width: '120px' }}>
                  <span className="fw-semibold text-dark">Total</span>
                </div>
                <div className="col-auto" style={{ width: '40px' }}>
                </div>
              </div>
            </div>

            {/* Table Rows */}
            {formData.items.map((item, index) => (
              <div
                key={index}
                className={`p-3 ${index < formData.items.length - 1 ? 'border-bottom' : ''}`}
              >
                <div className="row align-items-center g-3">
                  {/* Product Field */}
                  <div className="col">
                    <SearchableDropdownInput
                      id={`product-${index}`}
                      name={`product-${index}`}
                      value={item.product}
                      onChange={(e) => handleItemChange(index, 'product', e.target.value)}
                      options={getAvailableProducts(index)}
                      placeholder="Select product"
                      optionValueKey="value"
                      optionLabelKey="label"
                      required
                    />
                  </div>

                  {/* Price Field */}
                  <div className="col-auto" style={{ width: '120px' }}>
                    <input
                      type="number"
                      min="0"
                      placeholder="0.00"
                      value={item.price}
                      onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                      className="form-control"
                    />
                  </div>

                  {/* Quantity Field */}
                  <div className="col-auto" style={{ width: '150px' }}>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="form-control"
                    />
                  </div>

                  {/* Total Field (Read-only) */}
                  <div className="col-auto" style={{ width: '120px' }}>
                    <input
                      type="text"
                      value={(item.quantity * item.price || 0).toFixed(2)}
                      className="form-control"
                      readOnly
                    />
                  </div>

                  {/* Remove Button */}
                  <div className="col-auto" style={{ width: '40px' }}>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="btn btn-danger btn-sm"
                        style={{ width: '24px', height: '24px', padding: '0' }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div className="p-3 border-top">
              <div className="row justify-content-end">
                <div className="col-auto fw-semibold">Subtotal:</div>
                <div className="col-auto" style={{ width: '120px' }}>
                  {formData.items
                    .reduce((sum, item) => sum + (item.quantity * item.price || 0), 0)
                    .toFixed(2)}
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="d-flex justify-content-between">
          {/* Add Row Button */}
          <button
            type="button"
            onClick={addRow}
            className="btn btn-primary border mb-3 mt-3"
          >
            Add a row
          </button>

          {/* Save Button */}
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary border mb-3 mt-3"
            style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
            disabled={saveLoading}
          >
            {saveLoading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update' : 'Save')}
          </button>
        </div>
      </div>
    </>
  );
};

export default PurchaseAdd;